import { ApolloError } from "@apollo/client";
import {
  Divider,
  FormControl,
  Grid,
  GridItem,
  Select,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { useForm } from "react-hook-form";

import { Alert } from "../../../components";
import { UserRoleName } from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import useCurrentUser from "../../hooks/useCurrentUser";
import {
  ORGANIZATION_NOTIFICATION_SETTING_OPTIONS,
  OrganizationNotificationChannel,
  OrganizationNotificationSetting,
  OrganizationNotificationType,
} from "./notificationSettingsUtils";

export interface FormData {
  interviewRecapSubscriptionEmail: OrganizationNotificationSetting;
  interviewRecapSubscriptionSlack: OrganizationNotificationSetting;
  interviewRecapSubscriptionTeams: OrganizationNotificationSetting;
  interviewPrepSubscriptionEmail: OrganizationNotificationSetting;
  interviewPrepSubscriptionSlack: OrganizationNotificationSetting;
  interviewPrepSubscriptionTeams: OrganizationNotificationSetting;
  weeklyRecapSubscriptionEmail: OrganizationNotificationSetting;
  weeklyRecapSubscriptionSlack: OrganizationNotificationSetting;
  weeklyRecapSubscriptionTeams: OrganizationNotificationSetting;
  monthlyAnalyticsSubscriptionEmail: OrganizationNotificationSetting;
  monthlyAnalyticsSubscriptionTeams: OrganizationNotificationSetting;
  callShareSubscriptionEmail: OrganizationNotificationSetting;
  callShareSubscriptionSlack: OrganizationNotificationSetting;
  callShareSubscriptionTeams: OrganizationNotificationSetting;
  callNoteSubscriptionEmail: OrganizationNotificationSetting;
  callNoteSubscriptionSlack: OrganizationNotificationSetting;
  callNoteSubscriptionTeams: OrganizationNotificationSetting;
  morningDigestSubscriptionEmail: OrganizationNotificationSetting;
  morningDigestSubscriptionSlack: OrganizationNotificationSetting;
  morningDigestSubscriptionTeams: OrganizationNotificationSetting;
  coachingSubscriptionEmail: OrganizationNotificationSetting;
  coachingSubscriptionSlack: OrganizationNotificationSetting;
  coachingSubscriptionTeams: OrganizationNotificationSetting;
  candidateRediscoverySubscriptionEmail: OrganizationNotificationSetting;
  candidateRediscoverySubscriptionTeams: OrganizationNotificationSetting;
}

interface OrganizationNotificationsSettingsFormProps {
  interviewRecapSubscriptionEmail: OrganizationNotificationSetting;
  interviewRecapSubscriptionSlack: OrganizationNotificationSetting;
  interviewRecapSubscriptionTeams: OrganizationNotificationSetting;
  interviewPrepSubscriptionEmail: OrganizationNotificationSetting;
  interviewPrepSubscriptionSlack: OrganizationNotificationSetting;
  interviewPrepSubscriptionTeams: OrganizationNotificationSetting;
  weeklyRecapSubscriptionEmail: OrganizationNotificationSetting;
  weeklyRecapSubscriptionSlack: OrganizationNotificationSetting;
  weeklyRecapSubscriptionTeams: OrganizationNotificationSetting;
  monthlyAnalyticsSubscriptionEmail: OrganizationNotificationSetting;
  monthlyAnalyticsSubscriptionTeams: OrganizationNotificationSetting;
  callShareSubscriptionEmail: OrganizationNotificationSetting;
  callShareSubscriptionSlack: OrganizationNotificationSetting;
  callShareSubscriptionTeams: OrganizationNotificationSetting;
  callNoteSubscriptionEmail: OrganizationNotificationSetting;
  callNoteSubscriptionSlack: OrganizationNotificationSetting;
  callNoteSubscriptionTeams: OrganizationNotificationSetting;
  morningDigestSubscriptionEmail: OrganizationNotificationSetting;
  morningDigestSubscriptionSlack: OrganizationNotificationSetting;
  morningDigestSubscriptionTeams: OrganizationNotificationSetting;
  coachingSubscriptionEmail: OrganizationNotificationSetting;
  coachingSubscriptionSlack: OrganizationNotificationSetting;
  coachingSubscriptionTeams: OrganizationNotificationSetting;
  candidateRediscoverySubscriptionEmail: OrganizationNotificationSetting;
  candidateRediscoverySubscriptionTeams: OrganizationNotificationSetting;
  error: ApolloError | undefined;
  isLoading: boolean;
  onSettingChange: (
    notificationType: OrganizationNotificationType,
    channel: OrganizationNotificationChannel,
    setting: OrganizationNotificationSetting
  ) => void;
}

const OrganizationNotificationsSettingsForm: React.FC<
  OrganizationNotificationsSettingsFormProps
> = ({
  interviewRecapSubscriptionEmail,
  interviewRecapSubscriptionSlack,
  interviewRecapSubscriptionTeams,
  interviewPrepSubscriptionEmail,
  interviewPrepSubscriptionSlack,
  interviewPrepSubscriptionTeams,
  weeklyRecapSubscriptionEmail,
  weeklyRecapSubscriptionSlack,
  weeklyRecapSubscriptionTeams,
  monthlyAnalyticsSubscriptionEmail,
  monthlyAnalyticsSubscriptionTeams,
  callShareSubscriptionEmail,
  callShareSubscriptionSlack,
  callShareSubscriptionTeams,
  callNoteSubscriptionEmail,
  callNoteSubscriptionSlack,
  callNoteSubscriptionTeams,
  morningDigestSubscriptionEmail,
  morningDigestSubscriptionSlack,
  morningDigestSubscriptionTeams,
  coachingSubscriptionEmail,
  coachingSubscriptionSlack,
  coachingSubscriptionTeams,
  candidateRediscoverySubscriptionEmail,
  candidateRediscoverySubscriptionTeams,
  error,
  isLoading,
  onSettingChange,
}) => {
  const { register } = useForm<FormData>({
    defaultValues: {
      interviewRecapSubscriptionEmail,
      interviewRecapSubscriptionSlack,
      interviewRecapSubscriptionTeams,
      interviewPrepSubscriptionEmail,
      interviewPrepSubscriptionSlack,
      interviewPrepSubscriptionTeams,
      weeklyRecapSubscriptionEmail,
      weeklyRecapSubscriptionSlack,
      weeklyRecapSubscriptionTeams,
      monthlyAnalyticsSubscriptionEmail,
      monthlyAnalyticsSubscriptionTeams,
      callShareSubscriptionEmail,
      callShareSubscriptionSlack,
      callShareSubscriptionTeams,
      callNoteSubscriptionEmail,
      callNoteSubscriptionSlack,
      callNoteSubscriptionTeams,
      morningDigestSubscriptionEmail,
      morningDigestSubscriptionSlack,
      morningDigestSubscriptionTeams,
      coachingSubscriptionEmail,
      coachingSubscriptionSlack,
      coachingSubscriptionTeams,
      candidateRediscoverySubscriptionEmail,
      candidateRediscoverySubscriptionTeams,
    },
  });

  const currentUser = useCurrentUser();
  const { slackAppInstalled } = currentUser.organization;
  const { isTeamsAppInstalled } = currentUser;
  const isTeamsNotificationsEnabled =
    isTeamsAppInstalled &&
    currentUser.organization.teamsAppSettings?.allNotificationsEnabled;
  const coachingEnabled = useFeatureFlag("coaching:v1");
  const rediscoveryEnabled = useFeatureFlag("rediscovery:v1");

  const renderSelect = (
    fieldName: keyof FormData,
    defaultValue: OrganizationNotificationSetting,
    notificationType: OrganizationNotificationType,
    channel: OrganizationNotificationChannel,
    isDisabled?: boolean
  ): JSX.Element => (
    <Select
      {...register(fieldName, {
        onChange: (event) => {
          onSettingChange(
            notificationType,
            channel,
            event.target.value as OrganizationNotificationSetting
          );
        },
      })}
      defaultValue={defaultValue}
      isDisabled={isDisabled || isLoading}
    >
      {ORGANIZATION_NOTIFICATION_SETTING_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
  );

  return (
    <form>
      {error?.graphQLErrors?.map(({ message }, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Alert mb={6} key={i} status="error" description={message} />
      ))}
      <Grid
        h="auto"
        templateColumns="repeat(15, 1fr)"
        gridAutoRows="min-content"
        columnGap={14}
      >
        {/* Header */}
        <GridItem colSpan={5}>
          <Text color="gray.500" fontSize="sm" fontWeight="bold">
            Notify everyone when...
          </Text>
        </GridItem>
        <GridItem colSpan={3}>
          <Text color="gray.500" fontSize="sm" fontWeight="bold">
            via Email
          </Text>
        </GridItem>
        <GridItem colSpan={3}>
          <Text
            color="gray.500"
            fontSize="sm"
            fontWeight="bold"
            hidden={!slackAppInstalled}
          >
            via Slack
          </Text>
        </GridItem>
        <GridItem colSpan={3}>
          <Text
            color="gray.500"
            fontSize="sm"
            fontWeight="bold"
            hidden={!isTeamsNotificationsEnabled}
          >
            via Teams
          </Text>
        </GridItem>
        {/* interview recording ready */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            My interview recording is ready
          </Text>
          <Text fontSize="xs">
            Includes any timestamped notes, reactions, interview questions, and
            a link to view your full recording and transcript.
          </Text>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl id="interviewRecapSubscriptionEmail" top="25%">
            {renderSelect(
              "interviewRecapSubscriptionEmail",
              interviewRecapSubscriptionEmail,
              "INTERVIEW_RECAP",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="interviewRecapSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderSelect(
              "interviewRecapSubscriptionSlack",
              interviewRecapSubscriptionSlack,
              "INTERVIEW_RECAP",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="interviewRecapSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderSelect(
              "interviewRecapSubscriptionTeams",
              interviewRecapSubscriptionTeams,
              "INTERVIEW_RECAP",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="22px" colSpan={15}>
          <Divider borderColor="gray.200" />
        </GridItem>
        {/* upcoming interviews */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            I have upcoming interviews
          </Text>
          <Text fontSize="xs">
            Includes links to previous recordings for your candidate, and
            opt-out links for your upcoming interview recording.
          </Text>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl id="interviewPrepSubscriptionEmail" top="25%">
            {renderSelect(
              "interviewPrepSubscriptionEmail",
              interviewPrepSubscriptionEmail,
              "INTERVIEW_PREP",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="interviewPrepSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderSelect(
              "interviewPrepSubscriptionSlack",
              interviewPrepSubscriptionSlack,
              "INTERVIEW_PREP",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="interviewPrepSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderSelect(
              "interviewPrepSubscriptionTeams",
              interviewPrepSubscriptionTeams,
              "INTERVIEW_PREP",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="22px" colSpan={15}>
          <Divider borderColor="gray.200" />
        </GridItem>
        {/* weekly recap */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            My weekly recap is ready
          </Text>
          <Text fontSize="xs">
            Includes a summary of your total interview time that week, with
            links to all your recordings.
          </Text>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl id="weeklyRecapSubscriptionEmail" top="25%">
            {renderSelect(
              "weeklyRecapSubscriptionEmail",
              weeklyRecapSubscriptionEmail,
              "WEEKLY_RECAP",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="weeklyRecapSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderSelect(
              "weeklyRecapSubscriptionSlack",
              weeklyRecapSubscriptionSlack,
              "WEEKLY_RECAP",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="weeklyRecapSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderSelect(
              "weeklyRecapSubscriptionTeams",
              weeklyRecapSubscriptionTeams,
              "WEEKLY_RECAP",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="22px" colSpan={15}>
          <Divider borderColor="gray.200" />
        </GridItem>
        {/* monthly analytics recap */}
        {currentUser.userRole?.name === UserRoleName.SiteAdmin && (
          <>
            <GridItem mt="20px" colSpan={5}>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                My monthly insights recap is ready
              </Text>
              <Text fontSize="xs">
                Includes a summary of your total interviews for the month, with
                links to featured metrics.
              </Text>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl id="monthlyAnalyticsSubscriptionEmail" top="25%">
                {renderSelect(
                  "monthlyAnalyticsSubscriptionEmail",
                  monthlyAnalyticsSubscriptionEmail,
                  "MONTHLY_ANALYTICS",
                  "EMAIL"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl
                id="monthlyAnalyticsSubscriptionSlack"
                top="25%"
                hidden
              >
                <Select isDisabled />
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl
                id="monthlyAnalyticsSubscriptionTeams"
                top="25%"
                hidden
              >
                {renderSelect(
                  "monthlyAnalyticsSubscriptionTeams",
                  monthlyAnalyticsSubscriptionTeams,
                  "MONTHLY_ANALYTICS",
                  "TEAMS",
                  true
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="22px" colSpan={15}>
              <Divider borderColor="gray.200" />
            </GridItem>
          </>
        )}
        {/* morning digest */}
        {(slackAppInstalled || isTeamsNotificationsEnabled) && (
          <>
            <GridItem mt="20px" colSpan={5}>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                My morning digest is ready
              </Text>
              <Text fontSize="xs">
                Includes a list of your scheduled interviews for that day.
              </Text>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl id="morningDigestSubscriptionEmail" top="25%" hidden>
                {renderSelect(
                  "morningDigestSubscriptionEmail",
                  morningDigestSubscriptionEmail,
                  "MORNING_DIGEST",
                  "EMAIL",
                  true
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl
                id="morningDigestSubscriptionSlack"
                top="25%"
                hidden={!slackAppInstalled}
              >
                {renderSelect(
                  "morningDigestSubscriptionSlack",
                  morningDigestSubscriptionSlack,
                  "MORNING_DIGEST",
                  "SLACK"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl
                id="morningDigestSubscriptionTeams"
                top="25%"
                hidden={!isTeamsNotificationsEnabled}
              >
                {renderSelect(
                  "morningDigestSubscriptionTeams",
                  morningDigestSubscriptionTeams,
                  "MORNING_DIGEST",
                  "TEAMS"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="22px" colSpan={15}>
              <Divider borderColor="gray.200" />
            </GridItem>
          </>
        )}
        {/* call share */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Someone shares an interview, clip or playlist with me
          </Text>
          <Text fontSize="xs">
            Includes a link to the interview, clip or playlist that was shared.
          </Text>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl id="callShareSubscriptionEmail" top="25%">
            {renderSelect(
              "callShareSubscriptionEmail",
              callShareSubscriptionEmail,
              "CALL_SHARE",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="callShareSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderSelect(
              "callShareSubscriptionSlack",
              callShareSubscriptionSlack,
              "CALL_SHARE",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="callShareSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderSelect(
              "callShareSubscriptionTeams",
              callShareSubscriptionTeams,
              "CALL_SHARE",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="22px" colSpan={15}>
          <Divider borderColor="gray.200" />
        </GridItem>
        {/* call note */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Someone leaves a comment on my interview or clip
          </Text>
          <Text fontSize="xs">Includes a link to the comment.</Text>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl id="callNoteSubscriptionEmail" top="25%">
            {renderSelect(
              "callNoteSubscriptionEmail",
              callNoteSubscriptionEmail,
              "CALL_NOTE",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="callNoteSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderSelect(
              "callNoteSubscriptionSlack",
              callNoteSubscriptionSlack,
              "CALL_NOTE",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={3}>
          <FormControl
            id="callNoteSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderSelect(
              "callNoteSubscriptionTeams",
              callNoteSubscriptionTeams,
              "CALL_NOTE",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        {coachingEnabled && (
          <>
            <GridItem mt="22px" colSpan={15}>
              <Divider borderColor="gray.200" />
            </GridItem>
            {/* interviewing tips */}
            <GridItem mt="20px" colSpan={5}>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                Interviewing tips
              </Text>
              <Text fontSize="xs">
                Includes customized interviewing tips based on your recent
                interviews.
              </Text>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl id="coachingSubscriptionEmail" top="25%">
                {renderSelect(
                  "coachingSubscriptionEmail",
                  coachingSubscriptionEmail,
                  "COACHING",
                  "EMAIL"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl
                id="coachingSubscriptionSlack"
                top="25%"
                hidden={!slackAppInstalled}
              >
                {renderSelect(
                  "coachingSubscriptionSlack",
                  coachingSubscriptionSlack,
                  "COACHING",
                  "SLACK"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl id="coachingSubscriptionTeams" top="25%" hidden>
                {renderSelect(
                  "coachingSubscriptionTeams",
                  coachingSubscriptionTeams,
                  "COACHING",
                  "TEAMS",
                  true
                )}
              </FormControl>
            </GridItem>
          </>
        )}
        {rediscoveryEnabled && (
          <>
            <GridItem mt="22px" colSpan={15}>
              <Divider borderColor="gray.200" />
            </GridItem>
            {/* rediscovery */}
            <GridItem mt="20px" colSpan={5}>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                BrightHire identifies potential candidates for my role
              </Text>
              <Text fontSize="xs">
                Includes a link to a list of potential candidates.
              </Text>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl id="candidateRediscoverySubscriptionEmail" top="25%">
                {renderSelect(
                  "candidateRediscoverySubscriptionEmail",
                  candidateRediscoverySubscriptionEmail,
                  "CANDIDATE_REDISCOVERY",
                  "EMAIL"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl
                id="candidateRediscoverySubscriptionSlack"
                top="25%"
                hidden
              >
                <Select isDisabled />
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={3}>
              <FormControl
                id="candidateRediscoverySubscriptionTeams"
                top="25%"
                hidden
              >
                {renderSelect(
                  "candidateRediscoverySubscriptionTeams",
                  candidateRediscoverySubscriptionTeams,
                  "CANDIDATE_REDISCOVERY",
                  "TEAMS",
                  true
                )}
              </FormControl>
            </GridItem>
          </>
        )}
      </Grid>
    </form>
  );
};

export default OrganizationNotificationsSettingsForm;
